import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth, rolePortal } from "@/lib/auth";
import { canAccessAdminRoute, defaultAdminPath } from "@/lib/permissions";

import Login from "@/pages/Login";
import AdminLayout from "@/pages/admin/AdminLayout";
import Dashboard from "@/pages/admin/Dashboard";
import Companies from "@/pages/admin/Companies";
import Employees from "@/pages/admin/Employees";
import Contracts from "@/pages/admin/Contracts";
import Onboarding from "@/pages/admin/Onboarding";
import OrgChart from "@/pages/admin/OrgChart";
import MasterData from "@/pages/admin/MasterData";
import Tasks from "@/pages/admin/Tasks";
import Attendance from "@/pages/admin/Attendance";
import KPI from "@/pages/admin/KPI";
import Payroll from "@/pages/admin/Payroll";
import ExcelWorkspace from "@/pages/admin/ExcelWorkspace";
import InvoiceOCR from "@/pages/admin/InvoiceOCR";
import POS from "@/pages/admin/POS";
import Stock from "@/pages/admin/Stock";
import Kitchen from "@/pages/admin/Kitchen";
import HTX from "@/pages/admin/HTX";
import LegacyPayroll from "@/pages/admin/LegacyPayroll";
import Shifts from "@/pages/admin/Shifts";
import AuditHistory from "@/pages/admin/AuditHistory";
// F01 imports
import LegalVault from "@/pages/admin/LegalVault";
import LegalVaultRequests from "@/pages/admin/LegalVaultRequests";
import LegalVaultGrants from "@/pages/admin/LegalVaultGrants";
import LegalVaultAudit from "@/pages/admin/LegalVaultAudit";
import StaffLegalRequest from "@/pages/staff/StaffLegalRequest";
// F02 imports
import HRMPositions from "@/pages/admin/HRMPositions";
import HRMTraining from "@/pages/admin/HRMTraining";
import HRMDailyWork from "@/pages/admin/HRMDailyWork";
import HRMIncidents from "@/pages/admin/HRMIncidents";
import DailyTaskApproval from "@/pages/admin/DailyTaskApproval";
// F03 imports
import MenuGuideline from "@/pages/admin/MenuGuideline";

import StaffLayout from "@/pages/staff/StaffLayout";
import StaffHome from "@/pages/staff/StaffHome";
import StaffTasks from "@/pages/staff/StaffTasks";
import StaffAttendance from "@/pages/staff/StaffAttendance";
import StaffKPI from "@/pages/staff/StaffKPI";
import StaffPayslip from "@/pages/staff/StaffPayslip";
import StaffKDS from "@/pages/staff/StaffKDS";
import StaffSchedule from "@/pages/staff/StaffSchedule";

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-stone-400">Đang khởi tạo…</div>;
  }
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={rolePortal(user.role)} replace />;
}

function AdminRoute({ routeKey, children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-stone-400">Đang khởi tạo…</div>;
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!canAccessAdminRoute(user.role, routeKey)) {
    return <Navigate to={defaultAdminPath(user.role)} replace />;
  }
  return children;
}

function App() {
  return (
    <AuthProvider>
      <Toaster position="top-right" richColors closeButton />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/login" element={<Login />} />

          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminRoute routeKey="dashboard"><Dashboard /></AdminRoute>} />
            <Route path="companies" element={<AdminRoute routeKey="companies"><Companies /></AdminRoute>} />
            <Route path="employees" element={<AdminRoute routeKey="employees"><Employees /></AdminRoute>} />
            <Route path="contracts" element={<AdminRoute routeKey="contracts"><Contracts /></AdminRoute>} />
            <Route path="onboarding" element={<AdminRoute routeKey="onboarding"><Onboarding /></AdminRoute>} />
            <Route path="org-chart" element={<AdminRoute routeKey="org-chart"><OrgChart /></AdminRoute>} />
            <Route path="master" element={<AdminRoute routeKey="master"><MasterData /></AdminRoute>} />
            <Route path="tasks" element={<AdminRoute routeKey="tasks"><Tasks /></AdminRoute>} />
            <Route path="attendance" element={<AdminRoute routeKey="attendance"><Attendance /></AdminRoute>} />
            <Route path="shifts" element={<AdminRoute routeKey="shifts"><Shifts /></AdminRoute>} />
            <Route path="kpi" element={<AdminRoute routeKey="kpi"><KPI /></AdminRoute>} />
            <Route path="payroll" element={<AdminRoute routeKey="payroll"><Payroll /></AdminRoute>} />
            <Route path="excel" element={<AdminRoute routeKey="excel"><ExcelWorkspace /></AdminRoute>} />
            <Route path="ocr" element={<AdminRoute routeKey="ocr"><InvoiceOCR /></AdminRoute>} />
            <Route path="pos" element={<AdminRoute routeKey="pos"><POS /></AdminRoute>} />
            <Route path="stock" element={<AdminRoute routeKey="stock"><Stock /></AdminRoute>} />
            <Route path="kitchen" element={<AdminRoute routeKey="kitchen"><Kitchen /></AdminRoute>} />
            <Route path="htx" element={<AdminRoute routeKey="htx"><HTX /></AdminRoute>} />
            <Route path="legacy-payroll" element={<AdminRoute routeKey="legacy-payroll"><LegacyPayroll /></AdminRoute>} />
            <Route path="audit-history" element={<AdminRoute routeKey="audit-history"><AuditHistory /></AdminRoute>} />
            <Route path="legal-vault" element={<AdminRoute routeKey="legal-vault"><LegalVault /></AdminRoute>} />
            <Route path="legal-requests" element={<AdminRoute routeKey="legal-requests"><LegalVaultRequests /></AdminRoute>} />
            <Route path="legal-grants" element={<AdminRoute routeKey="legal-grants"><LegalVaultGrants /></AdminRoute>} />
            <Route path="legal-audit" element={<AdminRoute routeKey="legal-audit"><LegalVaultAudit /></AdminRoute>} />
            {/* === ADMIN ROUTES F01 === Legal Entity Document Vault — worker inserts <Route ... /> entries ABOVE this marker */}
            {/* === END ADMIN ROUTES F01 === */}
            <Route path="hrm-positions" element={<AdminRoute routeKey="hrm-positions"><HRMPositions /></AdminRoute>} />
            <Route path="hrm-training" element={<AdminRoute routeKey="hrm-training"><HRMTraining /></AdminRoute>} />
            <Route path="hrm-daily-work" element={<AdminRoute routeKey="hrm-daily-work"><HRMDailyWork /></AdminRoute>} />
            <Route path="hrm-incidents" element={<AdminRoute routeKey="hrm-incidents"><HRMIncidents /></AdminRoute>} />
            <Route path="daily-task-approval" element={<AdminRoute routeKey="daily-task-approval"><DailyTaskApproval /></AdminRoute>} />
            {/* === ADMIN ROUTES F02 === HRM Workforce Ops — worker inserts <Route ... /> entries ABOVE this marker */}
            {/* === END ADMIN ROUTES F02 === */}
            {/* F03 MENU-GUIDELINE START */}
            <Route path="menu-guideline" element={<AdminRoute routeKey="menu-guideline"><MenuGuideline /></AdminRoute>} />
            {/* F03 MENU-GUIDELINE END */}
          </Route>

          <Route path="/staff" element={<StaffLayout />}>
            <Route index element={<StaffHome />} />
            <Route path="tasks" element={<StaffTasks />} />
            <Route path="attendance" element={<StaffAttendance />} />
            <Route path="schedule" element={<StaffSchedule />} />
            <Route path="kpi" element={<StaffKPI />} />
            <Route path="payslip" element={<StaffPayslip />} />
            <Route path="kds" element={<StaffKDS />} />
            <Route path="legal-request" element={<StaffLegalRequest />} />
            {/* === STAFF ROUTES F01 === Legal request portal — worker inserts <Route ... /> ABOVE this marker */}
            {/* === END STAFF ROUTES F01 === */}
            <Route path="daily-work" element={<StaffTasks />} />
            {/* === STAFF ROUTES F02 === HRM staff daily-task surface — worker inserts <Route ... /> ABOVE this marker */}
            {/* === END STAFF ROUTES F02 === */}
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
